import { useEffect, useState } from "react";
import { supabase } from "@/shared/integrations/supabase/client";

export interface FilterOptions {
  categories: { id: string; name: string }[];
  statuses: string[];
}

/**
 * Fetch the available category and status filter values for the given
 * client's properties. Returns empty arrays until data loads.
 */
export function useFilterOptions(
  clientId: string | undefined,
  propertyIds: string[],
): FilterOptions {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    categories: [],
    statuses: [],
  });

  // Stable key so we re-run only when the actual ids change
  const propertyIdsKey = propertyIds.join(",");

  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (!clientId || propertyIds.length === 0) return;

      try {
        const { data } = await supabase
          .from("property_assets")
          .select("category_id, asset_categories(name), condition_status")
          .in("property_id", propertyIds);

        if (data) {
          const categoryMap = new Map<string, string>();
          for (const a of data) {
            const cat = Array.isArray(a.asset_categories)
              ? a.asset_categories[0]
              : a.asset_categories;
            if (a.category_id && cat?.name)
              categoryMap.set(a.category_id, cat.name);
          }
          const categories = Array.from(categoryMap.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((x, y) => x.name.localeCompare(y.name));
          const statuses = new Set(
            data
              .map((a) => a.condition_status)
              .filter((s): s is string => !!s),
          );

          setFilterOptions({
            categories,
            statuses: Array.from(statuses).sort(),
          });
        }
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };

    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, propertyIdsKey]);

  return filterOptions;
}
